import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotification } from '../../notification/NotificationContext';
import {
  checkAISessionAllowed,
  startAISession,
  endAISession,
  sendAIMessage,
  evaluateAISession,
} from '../../services/mockInterviewService';
import { parseFeedback } from './parseFeedback';

// Browser capability check. Firefox lacks SpeechRecognition entirely; Safari
// needs the webkit prefix. We expose `speechSupported` so the UI can degrade.
const SR = typeof window !== 'undefined'
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

const INITIAL_FORM = { jobRole: '', yearsOfExperience: '', techStack: '', about: '' };

export default function useAIInterview() {
  const { showNotification } = useNotification();

  const [phase, setPhase] = useState('intro'); // intro | live | evaluating | results
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [sessionId, setSessionId] = useState(null);
  const [history, setHistory] = useState([]); // { role: 'user'|'model', text }
  const [status, setStatus] = useState('idle'); // idle | listening | loading | speaking
  const [isAllowed, setIsAllowed] = useState(true);
  const [notAllowedReason, setNotAllowedReason] = useState('');
  const [checking, setChecking] = useState(true);
  const [score, setScore] = useState(null);
  const [feedback, setFeedback] = useState('');

  // Refs hold the mutable browser APIs + latest snapshots so async callbacks
  // don't close over stale state from the last render.
  const recognitionRef = useRef(null);
  const isListeningRef = useRef(false);
  const userStoppedRef = useRef(false);      // true when user presses Stop
  const finalTranscriptRef = useRef('');      // accumulated across restarts
  const voicesRef = useRef([]);
  const historyRef = useRef(history);
  const formRef = useRef(formData);
  const sessionIdRef = useRef(sessionId);
  const phaseRef = useRef(phase);

  useEffect(() => { historyRef.current = history; }, [history]);
  useEffect(() => { formRef.current = formData; }, [formData]);
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  const speechSupported = !!SR && typeof window !== 'undefined' && 'speechSynthesis' in window;

  // Voice list populates asynchronously on most browsers; cache it once.
  useEffect(() => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    const load = () => { voicesRef.current = window.speechSynthesis.getVoices() || []; };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  // Cleanup any in-flight audio / recognition when the component unmounts.
  useEffect(() => () => {
    userStoppedRef.current = true;
    isListeningRef.current = false;
    try { window.speechSynthesis?.cancel(); } catch {}
    try { recognitionRef.current?.abort(); } catch {}
  }, []);

  const speakText = useCallback((text) => new Promise((resolve) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      resolve();
      return;
    }
    // Cancel anything mid-speech so turns don't overlap.
    window.speechSynthesis.cancel();
    const clean = String(text || '').replace(/\[END\]/gi, '').trim();
    if (!clean) { resolve(); return; }

    const utterance = new SpeechSynthesisUtterance(clean);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.lang = 'en-US';

    const voices = voicesRef.current.length
      ? voicesRef.current
      : (window.speechSynthesis.getVoices() || []);
    const preferred =
      voices.find((v) => v.lang === 'en-US' && /female|zira|samantha|karen/i.test(v.name)) ||
      voices.find((v) => v.lang.startsWith('en')) ||
      voices[0];
    if (preferred) utterance.voice = preferred;

    // Chrome pauses speechSynthesis after ~15s on long text. Resume periodically.
    const resumeTimer = setInterval(() => {
      if (window.speechSynthesis.paused) window.speechSynthesis.resume();
    }, 10000);

    setStatus('speaking');
    utterance.onend = () => {
      clearInterval(resumeTimer);
      setStatus('idle');
      resolve();
    };
    utterance.onerror = () => {
      clearInterval(resumeTimer);
      setStatus('idle');
      resolve();
    };
    window.speechSynthesis.speak(utterance);
  }), []);

  const checkAllowed = useCallback(async () => {
    setChecking(true);
    try {
      const c = await checkAISessionAllowed();
      setIsAllowed(!!c?.allowed);
      setNotAllowedReason(c?.reason || '');
    } catch (e) {
      // Treat a failed check as allowed — the /start endpoint is the authority.
      setIsAllowed(true);
      setNotAllowedReason('');
    } finally {
      setChecking(false);
    }
  }, []);

  // Internal helper — called once on entering live phase to greet the candidate,
  // and re-used after every Speak click.
  const pushTurnAndCallAI = useCallback(async (userMessage) => {
    const priorHistory = historyRef.current;      // backend expects history BEFORE this turn
    const form = formRef.current;
    const sid = sessionIdRef.current;

    // Show user bubble immediately.
    setHistory((h) => [...h, { role: 'user', text: userMessage }]);
    setStatus('loading');

    try {
      const res = await sendAIMessage({
        sessionId: sid,
        userMessage,
        history: priorHistory,
        jobRole: form.jobRole,
        yearsOfExperience: form.yearsOfExperience,
        techStack: form.techStack,
        about: form.about,
      });

      const aiText = res?.aiMessage || '';
      setHistory((h) => [...h, { role: 'model', text: aiText }]);
      await speakText(aiText);

      if (res?.isInterviewComplete) {
        // Defer to caller via phase change — the page reacts to 'evaluating'.
        setPhase('evaluating');
      } else {
        setStatus('idle');
      }
    } catch (e) {
      setStatus('idle');
      showNotification(e.message || 'Interviewer is unreachable', 'error');
    }
  }, [showNotification, speakText]);

  const startSession = useCallback(async () => {
    const f = formData;
    if (!f.jobRole.trim() || !f.yearsOfExperience.trim() || !f.techStack.trim() || !f.about.trim()) {
      showNotification('All fields are required', 'warning');
      return;
    }
    try {
      const s = await startAISession({ sessionType: 'AI' });
      setSessionId(s.id);
      sessionIdRef.current = s.id;
      setHistory([]);
      historyRef.current = [];
      setScore(null);
      setFeedback('');
      setPhase('live');
      // Kick off: seed the conversation so the AI greets + asks Q1.
      await pushTurnAndCallAI('Hello, I am ready to start.');
    } catch (e) {
      showNotification(e.message || 'Failed to start session', 'error');
    }
  }, [formData, pushTurnAndCallAI, showNotification]);

  // Continuous-listen model: clicking Speak starts the recognizer and keeps
  // it running until the user clicks Stop. Chrome auto-ends a recognizer
  // after ~60s of silence; we transparently restart it so the user never
  // drops out mid-thought.
  const startListening = useCallback(() => {
    if (!SR) {
      showNotification('Voice input is not supported in this browser', 'error');
      return;
    }
    // Re-click while already listening = stop (prevents a second recognizer).
    if (isListeningRef.current) {
      userStoppedRef.current = true;
      try { recognitionRef.current?.stop(); } catch {}
      return;
    }
    if (status !== 'idle') return;

    finalTranscriptRef.current = '';
    userStoppedRef.current = false;

    const spawnRecognizer = () => {
      const rec = new SR();
      rec.lang = 'en-US';
      rec.continuous = true;       // keep listening through pauses
      rec.interimResults = true;    // so we can see mid-sentence activity
      rec.maxAlternatives = 1;

      rec.onresult = (e) => {
        // Append only finalized segments; interim chunks are ignored.
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const r = e.results[i];
          if (r.isFinal) {
            const t = r[0]?.transcript?.trim();
            if (t) {
              finalTranscriptRef.current =
                (finalTranscriptRef.current + ' ' + t).trim();
            }
          }
        }
      };

      rec.onerror = (ev) => {
        // 'no-speech' and 'aborted' are normal during a long listen. Mic
        // permission denial is the one case we surface to the user.
        if (ev?.error === 'not-allowed' || ev?.error === 'service-not-allowed') {
          userStoppedRef.current = true;
          showNotification('Microphone permission denied', 'error');
        }
      };

      rec.onend = () => {
        recognitionRef.current = null;
        if (userStoppedRef.current || !isListeningRef.current) {
          // User hit Stop (or unmount / phase change) — send accumulated.
          isListeningRef.current = false;
          const transcript = finalTranscriptRef.current.trim();
          finalTranscriptRef.current = '';
          if (transcript) pushTurnAndCallAI(transcript);
          else setStatus('idle');
        } else {
          // Chrome auto-ended on silence — quietly restart.
          try { spawnRecognizer(); } catch {
            isListeningRef.current = false;
            setStatus('idle');
          }
        }
      };

      recognitionRef.current = rec;
      try { rec.start(); } catch {
        isListeningRef.current = false;
        recognitionRef.current = null;
        setStatus('idle');
      }
    };

    isListeningRef.current = true;
    setStatus('listening');
    spawnRecognizer();
  }, [pushTurnAndCallAI, showNotification, status]);

  const stopListening = useCallback(() => {
    // Mark the user-stop intent; onend will send what we've accumulated.
    userStoppedRef.current = true;
    try { recognitionRef.current?.stop(); } catch {}
  }, []);

  // Runs /end + /evaluate in parallel and moves to the results phase.
  const runEvaluation = useCallback(async () => {
    try { window.speechSynthesis?.cancel(); } catch {}
    try { recognitionRef.current?.abort(); } catch {}
    const sid = sessionIdRef.current;
    const form = formRef.current;
    const priorHistory = historyRef.current;

    try {
      const [, evalRes] = await Promise.all([
        sid ? endAISession({ sessionId: sid }).catch(() => null) : Promise.resolve(null),
        evaluateAISession({
          sessionId:         sid ?? 0,
          history:           priorHistory,
          jobRole:           form.jobRole,
          techStack:         form.techStack,
          yearsOfExperience: form.yearsOfExperience,
          about:             form.about,
        }),
      ]);
      setScore(typeof evalRes?.score === 'number' ? evalRes.score : null);
      // Backend usually returns feedback as a plain string, but Gemini-shaped
      // envelopes have leaked through before. parseFeedback unwraps any of
      // them so the UI never renders raw JSON or "[object Object]".
      setFeedback(parseFeedback(evalRes?.feedback));
    } catch (e) {
      showNotification(e.message || 'Failed to evaluate session', 'error');
    } finally {
      setPhase('results');
      setStatus('idle');
    }
  }, [showNotification]);

  // The page renders its own confirm modal — this just triggers the wrap-up.
  const endSession = useCallback(() => {
    setPhase('evaluating');
  }, []);

  // Whenever the phase flips to 'evaluating' (either early-end or [END] signal
  // from the backend), fire the two wrap-up API calls exactly once.
  useEffect(() => {
    if (phase === 'evaluating') runEvaluation();
  }, [phase, runEvaluation]);

  const resetInterview = useCallback(() => {
    try { window.speechSynthesis?.cancel(); } catch {}
    try { recognitionRef.current?.abort(); } catch {}
    setSessionId(null);
    setHistory([]);
    setScore(null);
    setFeedback('');
    setStatus('idle');
    setPhase('intro');
    checkAllowed();
  }, [checkAllowed]);

  return {
    phase,
    formData, setFormData,
    sessionId,
    history,
    status,
    isAllowed,
    notAllowedReason,
    checking,
    score,
    feedback,
    speechSupported,
    checkAllowed,
    startSession,
    endSession,
    resetInterview,
    speakText,
    startListening,
    stopListening,
  };
}
